import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react';
import {
  Button,
  Card,
  CardBody,
  CardGap,
  CardHeader,
  ChoiceChip,
  Dropdown,
  Heading,
  IconButton,
  Text,
  TextInput,
  useOnClickOutside,
  XIcon,
  ChevronDownIcon
} from 'stubben-edge-unified-frontend-react-components';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useStore } from '@/hooks/useStore';
import type {
  SavedFilter,
  SavedFilterCriteria,
  SavedFilterSection
} from '@api-client/saved-filters';
import { savedFiltersQueryKeys } from '@api-client/saved-filters/query-keys';
import { useSavedFiltersQuery } from '@api-client/saved-filters/use-saved-filters-queries';
import {
  useCreateOrUpdateSavedFilterMutation,
  useDeleteSavedFilterMutation
} from '@api-client/saved-filters/use-saved-filters-mutations';
import type {
  DataTableProviderFilterItem,
  DataTableProviderFilterStateValue
} from './types';

export type SavedFiltersDrawerSectionHandle = {
  /**
   * Persist when Save/Update filter is selected (same as audiences Apply).
   * Returns false if persist was required but failed/validation blocked Apply.
   */
  persistIfNeeded: () => Promise<boolean>;
};

type SavedFiltersDrawerSectionProps = {
  section: SavedFilterSection;
  contextId?: number | null;
  filtersList: DataTableProviderFilterItem[];
  filtersState: Map<string, DataTableProviderFilterStateValue>;
  onHydrateFilters: (
    next: Map<string, DataTableProviderFilterStateValue>
  ) => void;
  /** Filter fields rendered between chips (top) and Save filter actions (bottom). */
  children?: ReactNode;
  onReset?: () => void;
  testIdentifierPrefix?: string;
};

function criteriaFromFiltersState(
  filtersState: Map<string, DataTableProviderFilterStateValue>
): SavedFilterCriteria {
  const criteria: SavedFilterCriteria = {};
  filtersState.forEach((state, key) => {
    if (
      state.value === '' ||
      state.value === null ||
      state.value === undefined
    ) {
      return;
    }
    criteria[key] = state.value as string | number | string[];
  });
  return criteria;
}

function filtersStateFromCriteria(
  criteria: SavedFilterCriteria,
  filtersList: DataTableProviderFilterItem[]
): Map<string, DataTableProviderFilterStateValue> {
  const next = new Map<string, DataTableProviderFilterStateValue>();

  Object.entries(criteria).forEach(([key, value]) => {
    const item = filtersList.find((filter) => filter.filterKey === key);
    if (!item) return;

    const option = item.selectOptions?.find(
      (opt) => `${opt.value}` === `${value}`
    );

    next.set(key, {
      item,
      value: value as string | number | string[],
      label: option?.label ?? String(value)
    });
  });

  return next;
}

const SavedFiltersDrawerSection = forwardRef<
  SavedFiltersDrawerSectionHandle,
  SavedFiltersDrawerSectionProps
>(function SavedFiltersDrawerSection(
  {
    section,
    contextId = null,
    filtersList,
    filtersState,
    onHydrateFilters,
    children,
    onReset,
    testIdentifierPrefix = 'saved-filters'
  },
  ref
) {
  const { toast } = useStore();
  const queryClient = useQueryClient();
  const [selectedFilterId, setSelectedFilterId] = useState<number | null>(null);
  const [selectedFilterOption, setSelectedFilterOption] = useState('');
  const [filterName, setFilterName] = useState('');
  const [saveFilterModalOpen, setSaveFilterModalOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useOnClickOutside<HTMLDivElement>(
    [wrapperRef, buttonRef, dropdownRef],
    () => setDropdownOpen(false)
  );

  const { data: savedFilters = [] } = useSavedFiltersQuery(section, contextId);

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: savedFiltersQueryKeys.list(section, contextId)
    });

  const { mutateAsync: createOrUpdate, isPending: isSaving } =
    useCreateOrUpdateSavedFilterMutation();

  const { mutate: deleteFilter, isPending: isDeleting } =
    useDeleteSavedFilterMutation({
      onSuccess: () => {
        toast.success('Filter deleted');
        setSelectedFilterId(null);
        setFilterName('');
        setSelectedFilterOption('');
        invalidate();
      },
      onError: (error) => {
        if (isAxiosError(error)) {
          toast.error(error.response?.data?.message || 'Error deleting filter');
        }
      }
    });

  const selectedFilter = useMemo(
    () => savedFilters.find((filter) => filter.id === selectedFilterId) ?? null,
    [savedFilters, selectedFilterId]
  );

  const hasFilterValues = useMemo(
    () => Object.keys(criteriaFromFiltersState(filtersState)).length > 0,
    [filtersState]
  );

  const handleSelectChip = (filter: SavedFilter) => {
    setSelectedFilterId(filter.id);
    setFilterName(filter.name);
    onHydrateFilters(filtersStateFromCriteria(filter.criteria, filtersList));
  };

  const handleSelectFilterOption = (value: string | number) => {
    const option = String(value);
    setSelectedFilterOption(option);
    setDropdownOpen(false);
    if (option === 'save-filter') {
      setSaveFilterModalOpen(true);
      if (!filterName && selectedFilter) setFilterName(selectedFilter.name);
    } else {
      setSaveFilterModalOpen(false);
    }
  };

  const handleResetClick = () => {
    setSelectedFilterId(null);
    setSelectedFilterOption('');
    setFilterName('');
    setSaveFilterModalOpen(false);
    setErrors({});
    onReset?.();
  };

  useImperativeHandle(ref, () => ({
    persistIfNeeded: async () => {
      const shouldPersist =
        selectedFilterOption === 'save-filter' ||
        selectedFilterOption === 'update-filter';

      if (!shouldPersist) return true;

      const criteria = criteriaFromFiltersState(filtersState);
      if (Object.keys(criteria).length === 0) {
        toast.error('Add at least one filter value before saving');
        return false;
      }

      if (selectedFilterOption === 'save-filter' && !filterName.trim()) {
        setErrors({ name: ['Filter name is required'] });
        setSaveFilterModalOpen(true);
        return false;
      }

      try {
        const saved = await createOrUpdate({
          id:
            selectedFilterOption === 'update-filter'
              ? selectedFilterId ?? undefined
              : undefined,
          section,
          context_id: contextId,
          name:
            selectedFilterOption === 'update-filter'
              ? selectedFilter?.name || filterName.trim()
              : filterName.trim(),
          criteria
        });
        toast.success('Filter saved');
        setSelectedFilterId(saved.id);
        setFilterName(saved.name);
        setSaveFilterModalOpen(false);
        setSelectedFilterOption('');
        setErrors({});
        invalidate();
        return true;
      } catch (error) {
        if (isAxiosError(error)) {
          setErrors(error.response?.data?.details ?? {});
          toast.error(error.response?.data?.message || 'Error saving filter');
        }
        return false;
      }
    }
  }));

  return (
    <div className="flex flex-col gap-6">
      {savedFilters.length > 0 && (
        <div className="flex flex-col gap-2">
          <Text size="sm" className="text-gray-600">
            Saved filters
          </Text>
          <div className="flex flex-wrap gap-2">
            {savedFilters.map((filter) => (
              <ChoiceChip
                key={filter.id}
                size="sm"
                selected={selectedFilterId === filter.id}
                onClick={() => handleSelectChip(filter)}
                testIdentifier={`${testIdentifierPrefix}-chip-${filter.id}`}
              >
                {filter.name}
              </ChoiceChip>
            ))}
          </div>
        </div>
      )}

      {children}

      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <div ref={wrapperRef} className="relative">
            <Button
              ref={buttonRef}
              variant="secondary"
              rightSlot={<ChevronDownIcon size={16} />}
              onClick={() => setDropdownOpen(!dropdownOpen)}
              testIdentifier={`${testIdentifierPrefix}-options`}
            >
              {selectedFilterOption === 'update-filter'
                ? 'Update filter'
                : 'Save filter'}
            </Button>
            <Dropdown
              ref={dropdownRef}
              open={dropdownOpen}
              options={[
                {
                  label: 'Save filter',
                  value: 'save-filter',
                  disabled: !hasFilterValues
                },
                {
                  label: 'Update filter',
                  value: 'update-filter',
                  disabled: !selectedFilterId || !hasFilterValues
                }
              ]}
              selectedOptions={[selectedFilterOption]}
              handleSelectOption={handleSelectFilterOption}
              position="right"
            />
          </div>
          <Button
            variant="link"
            onClick={handleResetClick}
            testIdentifier={`${testIdentifierPrefix}-reset`}
          >
            Reset
          </Button>
          {selectedFilterId && (
            <Button
              variant="link"
              disabled={isDeleting || isSaving}
              loading={isDeleting}
              onClick={() => deleteFilter(selectedFilterId)}
              testIdentifier={`${testIdentifierPrefix}-delete`}
            >
              Delete filter
            </Button>
          )}
        </div>

        {saveFilterModalOpen && (
          <Card className="border-[#95BFFF]">
            <CardHeader
              leftSlot={
                <Heading size="xxs" mode="brand">
                  Save new filter
                </Heading>
              }
              rightSlot={
                <IconButton
                  icon={<XIcon />}
                  mode="ghost"
                  onClick={() => setSaveFilterModalOpen(false)}
                  testIdentifier={`${testIdentifierPrefix}-close-name`}
                />
              }
            />
            <CardBody gap={CardGap.Large}>
              <TextInput
                label="Filter name"
                value={filterName}
                onChange={(value) => setFilterName(value)}
                error={!!errors.name}
                helperText={errors.name?.[0]}
                testIdentifier={`${testIdentifierPrefix}-name`}
              />
            </CardBody>
          </Card>
        )}
      </div>
    </div>
  );
});

export default SavedFiltersDrawerSection;
